import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFetchCollaterals } from './useFetchCollaterals';

import { Store } from '@/shared/store';
import { getAssetsToSupplyAction } from '@/shared/store/supplies/actions';

type GetAssetsToCollateralProps = {
    agreementAddress: string;
    role: string;
};

export const useGetAssetsToCollateral = ({
    agreementAddress,
    role,
}: GetAssetsToCollateralProps) => {
    const dispatch = useDispatch();

    const { data: userCollaterals } = useFetchCollaterals({ agreementAddress });

    const { agreements, blockchainName, walletAddress, type, loading } = useSelector(
        (state: Store) => ({
            agreements: state.agreements.borrowerAgreements,
            blockchainName: state.wallets.network,
            walletAddress: state.wallets.active.address,
            type: state.wallets.active.type,
            loading: state.agreements.agreementsLoading,
        }),
    );

    useEffect(() => {
        dispatch(getAssetsToSupplyAction());
    }, [type, blockchainName, walletAddress]);

    const agreement = agreements.filter(
        (agreement) => agreement.address === agreementAddress && agreement.role === role,
    )[0];

    const assetsToCollateralAddresses = agreement.collaterals;

    const assetsToCollateral = useMemo(
        () =>
            userCollaterals.filter((token) => assetsToCollateralAddresses.includes(token.address)),
        [userCollaterals, assetsToCollateralAddresses, walletAddress],
    );

    const sortedAssetsToCollateral = useMemo(() => {
        return assetsToCollateral.sort((collateralAssets1, collateralAssets2) => {
            return Number(collateralAssets2.balance) - Number(collateralAssets1.balance);
        });
    }, [assetsToCollateral, walletAddress]);

    return {
        data: sortedAssetsToCollateral,
        agreement,
        loading,
    };
};
